import type { Zone } from '@tsagko/label-contracts'
import { clampSplitRatio } from '../block-configurator/dimension-ops'
import { computeZoneLayout } from '../canvas/layout'
import { findZoneByPath, resolveBoxAtPath, setSplitRatioAtPath } from '../canvas/tree-ops'

export interface Floor {
  mm: number
  blockId: string
  instanceId: string
}

/**
 * Walk the subtree and emit one Floor entry per baked-instance leaf. The
 * axis selects placement.widthMm (`'row'` — children flow horizontally,
 * width matters) or placement.heightMm (`'column'`). Plain leaves and
 * split nodes emit nothing.
 */
export function collectFloors(zone: Zone, axis: 'row' | 'column'): Floor[] {
  const out: Floor[] = []
  function recurse(z: Zone): void {
    if (z.component?.type === 'baked-block-instance') {
      const p = z.component.placement
      out.push({
        mm: axis === 'row' ? p.widthMm : p.heightMm,
        blockId: z.component.blockId,
        instanceId: z.component.id,
      })
      return
    }
    if (z.children) {
      recurse(z.children[0])
      recurse(z.children[1])
    }
  }
  recurse(zone)
  return out
}

export function maxFloorMm(floors: Floor[]): number {
  let max = 0
  for (const f of floors) if (f.mm > max) max = f.mm
  return max
}

export interface FloorClampResult {
  ratio: number
  clampedSide?: 'a' | 'b'
}

/**
 * Apply a floor-aware split ratio change for the split zone at `path`.
 *
 * Resolves the parent zone and its bounding box, collects the floor from each
 * child along the split axis, clamps the raw ratio to honour those floors, and
 * returns a new rootZone with the ratio applied.
 *
 * Returns `rootZone` unchanged when the zone at `path` is not a split node.
 */
export function applyFloorClampToSplit(
  rootZone: Zone,
  templateDims: { widthMm: number; heightMm: number },
  path: string[],
  rawRatio: number,
): Zone {
  return applyFloorClampToSplitWithClampInfo(rootZone, templateDims, path, rawRatio).zone
}

export interface SplitClampInfo {
  zone: Zone
  clampedSide?: 'a' | 'b'
  clampingFloor?: Floor
}

/**
 * Like `applyFloorClampToSplit` but also returns clamp metadata: which side
 * was clamped and the Floor entry (blockId + instanceId) that drove the limit.
 * Used by the route to fire an info toast when a divider drag hits a floor.
 */
export function applyFloorClampToSplitWithClampInfo(
  rootZone: Zone,
  templateDims: { widthMm: number; heightMm: number },
  path: string[],
  rawRatio: number,
): SplitClampInfo {
  const parent = findZoneByPath(rootZone, path)
  if (!parent || !parent.split || !parent.children) return { zone: rootZone }
  const parentBox = resolveBoxAtPath(
    rootZone,
    path,
    { x: 0, y: 0, w: templateDims.widthMm, h: templateDims.heightMm },
  )
  if (!parentBox) return { zone: rootZone }
  const axis = parent.split.direction
  const parentTotal = axis === 'row' ? parentBox.w : parentBox.h
  const floorsA = collectFloors(parent.children[0], axis)
  const floorsB = collectFloors(parent.children[1], axis)
  const floorA = maxFloorMm(floorsA)
  const floorB = maxFloorMm(floorsB)
  const clamped = clampSplitRatioWithFloor(rawRatio, parentTotal, floorA, floorB)
  const zone = setSplitRatioAtPath(rootZone, path, clamped.ratio)
  if (!clamped.clampedSide) return { zone }
  // Find the Floor entry with the highest mm in the clamped child's subtree.
  const clampingFloors = clamped.clampedSide === 'a' ? floorsA : floorsB
  const clampingFloor = clampingFloors.reduce<Floor | undefined>(
    (best, f) => (best === undefined || f.mm > best.mm ? f : best),
    undefined,
  )
  return { zone, clampedSide: clamped.clampedSide, clampingFloor }
}

/**
 * Clamp a raw split ratio so that both children honour their floors.
 *
 * Floor A is the largest baked-instance dimension that lives in child A
 * along the split axis; floor B is the same for child B. Caller obtains
 * each via `maxFloorMm(collectFloors(child, axis))`.
 *
 * Order of operations:
 *   1. Apply the schema clamp `[0.05, 0.95]` to the raw ratio.
 *   2. If parentTotal <= 0, return the clamped raw with no floor work.
 *   3. Compute minRatio = floorA / parentTotal, maxRatio = 1 - floorB / parentTotal.
 *   4. If minRatio > maxRatio (floors don't fit) — give floor A everything it
 *      asks for (ratio = minRatio, clampedSide 'a'); B's lint will catch
 *      the remaining overflow downstream.
 *   5. Else clamp into [minRatio, maxRatio], also intersected with [0.05, 0.95],
 *      and mark `clampedSide` accordingly.
 */
export function clampSplitRatioWithFloor(
  rawRatio: number,
  parentTotalMm: number,
  floorAMm: number,
  floorBMm: number,
): FloorClampResult {
  const baseClamped = clampSplitRatio(rawRatio)
  if (parentTotalMm <= 0) return { ratio: baseClamped }
  const minFromFloor = floorAMm > 0 ? floorAMm / parentTotalMm : 0
  const maxFromFloor = floorBMm > 0 ? 1 - floorBMm / parentTotalMm : 1
  const minRatio = Math.max(0.05, minFromFloor)
  const maxRatio = Math.min(0.95, maxFromFloor)
  if (minRatio > maxRatio) {
    return { ratio: clampSplitRatio(minRatio), clampedSide: 'a' }
  }
  if (baseClamped < minRatio) return { ratio: minRatio, clampedSide: 'a' }
  if (baseClamped > maxRatio) return { ratio: maxRatio, clampedSide: 'b' }
  return { ratio: baseClamped }
}

// Designer label-dimension bounds — match clampBlockDimensionMm (1..300 mm).
const MIN_LABEL_MM = 1
const MAX_LABEL_MM = 300
// Reference size for the floor probe. The label dimension cancels out of
// `requiredMm / cellMm × labelMm`, so any positive value works; 1000 keeps
// float precision comfortable.
const FLOOR_REF_MM = 1000

/**
 * Smallest label size (per axis) at which no placed block overflows its
 * current cell, given the current split ratios.
 *
 * A leaf's cell size scales linearly with the label size, so the per-leaf
 * floor `placement.size / cellFraction` is independent of the current label
 * size — making this a pure function of the zone tree. We probe the tree at a
 * fixed reference size via `computeZoneLayout`, read each block's host cell
 * (the `bakedDepth === 0` interactive leaf — interior expansion leaves carry
 * `bakedDepth >= 1` and are excluded), and take the per-axis maximum.
 *
 * Returns `{ widthMm: 1, heightMm: 1 }` (schema min) when there are no blocks.
 * Each floor is rounded up (so we never round under the true floor) and capped
 * at 300 mm (schema max) so an impossible layout — a block larger than its
 * ratio cell even at the max label — degrades to a best-effort clamp plus the
 * existing lint warning, rather than producing `min > max` at the call site.
 */
export function computeMinLabelSizeMm(rootZone: Zone): { widthMm: number; heightMm: number } {
  const { widthMm, heightMm } = computeMinLabelSizeWithConstraint(rootZone)
  return { widthMm, heightMm }
}

export interface MinLabelSizeWithConstraint {
  widthMm: number
  heightMm: number
  /** The Floor entry (blockId + instanceId) that drives the width floor. */
  widthConstraint: Floor | undefined
  /** The Floor entry (blockId + instanceId) that drives the height floor. */
  heightConstraint: Floor | undefined
}

/**
 * Like `computeMinLabelSizeMm` but also returns the Floor entries (blockId +
 * instanceId) that drive the per-axis limits. Used by the route to fire an
 * info toast when a ruler input or drag commits a clamped value.
 */
export function computeMinLabelSizeWithConstraint(rootZone: Zone): MinLabelSizeWithConstraint {
  const layout = computeZoneLayout(rootZone, { widthMm: FLOOR_REF_MM, heightMm: FLOOR_REF_MM })
  let wFactor = 0
  let hFactor = 0
  let wConstraint: Floor | undefined
  let hConstraint: Floor | undefined
  for (const z of layout) {
    if (z.bakedDepth === 0 && z.component?.type === 'baked-block-instance') {
      const p = z.component.placement
      if (z.w > 0) {
        const wf = p.widthMm / z.w
        if (wf > wFactor) {
          wFactor = wf
          wConstraint = { mm: p.widthMm, blockId: z.component.blockId, instanceId: z.component.id }
        }
      }
      if (z.h > 0) {
        const hf = p.heightMm / z.h
        if (hf > hFactor) {
          hFactor = hf
          hConstraint = { mm: p.heightMm, blockId: z.component.blockId, instanceId: z.component.id }
        }
      }
    }
  }
  return {
    widthMm: wFactor > 0 ? Math.min(MAX_LABEL_MM, Math.ceil(FLOOR_REF_MM * wFactor)) : MIN_LABEL_MM,
    heightMm: hFactor > 0 ? Math.min(MAX_LABEL_MM, Math.ceil(FLOOR_REF_MM * hFactor)) : MIN_LABEL_MM,
    widthConstraint: wConstraint,
    heightConstraint: hConstraint,
  }
}
